import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Alert, Card, Spinner } from 'react-bootstrap';
import aiService from '../services/aiService';
import authService from '../services/authService';
import businessService from '../services/businessService';
import SpeechInput from '../components/SpeechInput';
import BackToDashboard from '../components/BackToDashboard';

function CoreValues() {
  const navigate = useNavigate();
  const [stage, setStage] = useState('intro'); // intro, conversation, extracted, refine, complete
  const [conversation, setConversation] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [questionNumber, setQuestionNumber] = useState(0);
  const [extractedValues, setExtractedValues] = useState([]);
  const [selectedValues, setSelectedValues] = useState(new Set()); // Track which values user selects
  const [readyToExtract, setReadyToExtract] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [savedConversation, setSavedConversation] = useState([]);

  // Speech input ref
  const inputBeforeSpeechRef = useRef('');

  // Mission/Vision extraction
  const [extractedMission, setExtractedMission] = useState('');
  const [extractedVision, setExtractedVision] = useState('');
  const [editedMission, setEditedMission] = useState('');
  const [editedVision, setEditedVision] = useState('');
  const [missionVisionLoading, setMissionVisionLoading] = useState(false);
  const [missionVisionSaved, setMissionVisionSaved] = useState(false);

  useEffect(() => {
    checkAuthAndPrerequisites();
  }, []);

  const checkAuthAndPrerequisites = async () => {
    // Check authentication
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Check if business basics is completed
    const statusResult = await businessService.checkBusinessBasicsStatus();
    if (!statusResult.completed) {
      setError('Please complete Business Basics before starting Core Values Discovery');
      return;
    }

    // Check if user has existing core values
    const valuesResult = await aiService.getCoreValues();
    if (valuesResult.success && valuesResult.data.values && valuesResult.data.values.length > 0) {
      setExtractedValues(valuesResult.data.values);
      setStage('complete');
    }
  };

  const startConversation = async () => {
    setLoading(true);
    setError('');

    const result = await aiService.startCoreValues();

    setLoading(false);

    if (result.success) {
      setConversation(result.data.conversation);
      setQuestionNumber(result.data.question_number);
      setStage('conversation');
    } else {
      setError(result.error);
    }
  };

  const sendResponse = async () => {
    if (!userInput.trim()) {
      setError('Please enter a response');
      return;
    }

    setLoading(true);
    setError('');

    const result = await aiService.respondCoreValues(userInput, conversation);

    setLoading(false);

    if (result.success) {
      setConversation(result.data.conversation);
      setQuestionNumber(result.data.question_number);
      setUserInput('');
      setReadyToExtract(result.data.ready_to_extract);
    } else {
      setError(result.error);
    }
  };

  const extractValues = async () => {
    setLoading(true);
    setError('');

    const result = await aiService.extractCoreValues(conversation);

    setLoading(false);

    if (result.success) {
      setExtractedValues(result.data.values);
      setStage('extracted');
    } else {
      setError(result.error);
    }
  };

  const acceptValues = () => {
    setStage('complete');
  };

  const refineValues = () => {
    setStage('refine');
  };

  const saveRefinedValues = async () => {
    // Save all refined values
    setStage('complete');
  };

  const loadSavedConversation = async () => {
    try {
      const response = await fetch('/api/generation/conversation/core_values', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSavedConversation(data.conversation || []);
        setShowTranscript(true);
      }
    } catch (err) {
      console.error('Error loading conversation:', err);
    }
  };

  const renderIntro = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">🎯 WHAT ARE CORE VALUES (REALLY)?</h2>

        <p>
          Core values aren't corporate buzzwords like "integrity" and "excellence"
          that every company claims. They're the principles you'd defend even when
          it's inconvenient or expensive.
        </p>

        <h5 className="mt-4">Core values are:</h5>
        <ul>
          <li>Decision-making filters (when in doubt, which value wins?)</li>
          <li>Hiring criteria (do they share these beliefs?)</li>
          <li>Walkaway triggers (what would make you refuse a client?)</li>
          <li>Culture definers (how people behave when no one's watching)</li>
        </ul>

        <h5 className="mt-4">Core values are NOT:</h5>
        <ul>
          <li>What sounds impressive</li>
          <li>What competitors say</li>
          <li>Generic aspirations</li>
          <li>Marketing copy</li>
        </ul>

        <h5 className="mt-4">Examples of REAL core values:</h5>

        <div className="mb-3">
          <strong>Generic:</strong> "Customer-focused"<br/>
          <strong>Specific:</strong> "We respond within 24 hours, even if it's bad news"
        </div>

        <div className="mb-3">
          <strong>Generic:</strong> "Innovation"<br/>
          <strong>Specific:</strong> "We ship imperfect products to learn fast, then iterate"
        </div>

        <div className="mb-4">
          <strong>Generic:</strong> "Quality"<br/>
          <strong>Specific:</strong> "We'd rather lose a sale than deliver something we're not proud of"
        </div>

        <hr/>

        <h4 className="mt-4">💬 DISCOVERY PROCESS</h4>

        <p>
          We'll have a conversation where I ask you questions about how you make
          decisions, what matters most to you, and what you won't compromise on.
        </p>

        <p>
          There are no right answers - just honest ones.
        </p>

        <p className="mb-4">
          This takes about 10-15 minutes. Ready?
        </p>

        <Button variant="primary" size="lg" onClick={startConversation} disabled={loading}>
          {loading ? 'Starting...' : 'Start Core Values Discovery'}
        </Button>
      </Card.Body>
    </Card>
  );

  const renderConversation = () => {
    const aiMessages = conversation.filter(msg => msg.role === 'assistant');
    const lastAiMessage = aiMessages[aiMessages.length - 1];

    return (
      <Card className="shadow">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>💬 CORE VALUES DISCOVERY</h2>
            <small className="text-muted">Question {questionNumber} of ~8-10</small>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          <div className="mb-4 p-3 bg-light rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <strong>🤖 AI Coach:</strong>
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted mb-0">Thinking...</p>
              </div>
            ) : (
              <div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>{lastAiMessage?.content}</div>
            )}
          </div>

          <Form.Group className="mb-3">
            <Form.Label><strong>You:</strong></Form.Label>
            <div className="position-relative">
              <Form.Control
                as="textarea"
                rows={5}
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                placeholder="Share your thoughts here..."
                disabled={loading}
                style={{ paddingRight: '50px' }}
              />
              <SpeechInput
                onStart={() => {
                  inputBeforeSpeechRef.current = userInput;
                }}
                onTranscript={(text, isFinal) => {
                  const prefix = inputBeforeSpeechRef.current ? inputBeforeSpeechRef.current + ' ' : '';
                  setUserInput(prefix + text);
                }}
                onError={(msg) => setError(msg)}
                disabled={loading}
                className="position-absolute"
                style={{ right: '8px', bottom: '8px' }}
                size="sm"
              />
            </div>
          </Form.Group>

          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <Button
              variant="outline-secondary"
              onClick={() => navigate('/dashboard/foundation')}
              disabled={loading}
            >
              Save & Continue Later
            </Button>

            <div className="d-flex gap-2">
              {/* Always show "I'm Done" button after a few exchanges */}
              {questionNumber >= 2 && (
                <Button
                  variant="success"
                  onClick={extractValues}
                  disabled={loading}
                  title="Extract values from what you've shared so far"
                >
                  {loading ? 'Extracting...' : "I'm Done - Extract Values"}
                </Button>
              )}

              <Button
                variant="primary"
                onClick={sendResponse}
                disabled={loading || !userInput.trim()}
              >
                {loading ? 'Sending...' : 'Send'}
              </Button>
            </div>
          </div>

          {questionNumber >= 2 && (
            <div className="text-center mt-3">
              <small className="text-muted">
                Shared enough? Click "I'm Done" to extract your core values from the conversation so far.
              </small>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  const toggleValueSelection = (index) => {
    const newSelected = new Set(selectedValues);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedValues(newSelected);
  };

  const selectAllValues = () => {
    setSelectedValues(new Set(extractedValues.map((_, i) => i)));
  };

  const acceptSelectedValues = async () => {
    // Filter to only keep selected values
    const selectedValuesArray = extractedValues.filter((_, index) => selectedValues.has(index));
    setExtractedValues(selectedValuesArray);
    setStage('complete');

    // Trigger mission/vision extraction from the conversation
    setMissionVisionLoading(true);
    try {
      const response = await fetch('/api/generation/extract-mission-vision', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          conversation: conversation,
          core_values: selectedValuesArray
        })
      });

      if (response.ok) {
        const data = await response.json();
        setExtractedMission(data.mission || '');
        setExtractedVision(data.vision || '');
        setEditedMission(data.mission || '');
        setEditedVision(data.vision || '');
      }
    } catch (err) {
      console.error('Error extracting mission/vision:', err);
    } finally {
      setMissionVisionLoading(false);
    }
  };

  const saveMissionVision = async () => {
    try {
      const response = await fetch('/api/generation/save-mission-vision', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          mission: editedMission,
          vision: editedVision
        })
      });

      if (response.ok) {
        setMissionVisionSaved(true);
      }
    } catch (err) {
      console.error('Error saving mission/vision:', err);
    }
  };

  const discardMissionVision = () => {
    setExtractedMission('');
    setExtractedVision('');
    setEditedMission('');
    setEditedVision('');
  };

  const renderExtractedValues = () => {
    // Initialize all values as selected when first shown
    if (selectedValues.size === 0 && extractedValues.length > 0) {
      setSelectedValues(new Set(extractedValues.map((_, i) => i)));
    }

    return (
      <Card className="shadow">
        <Card.Body className="p-4">
          <h2 className="mb-3">✨ YOUR CORE VALUES</h2>
          <p className="text-muted mb-2">We found {extractedValues.length} values from our conversation.</p>
          <p className="mb-4">
            <strong>Select the ones you want to keep</strong> - uncheck any that don't resonate or feel redundant.
          </p>

          {error && <Alert variant="danger">{error}</Alert>}

          <div className="d-flex justify-content-end mb-3">
            <Button variant="link" size="sm" onClick={selectAllValues}>
              Select All
            </Button>
            <Button variant="link" size="sm" onClick={() => setSelectedValues(new Set())}>
              Deselect All
            </Button>
          </div>

          {extractedValues.map((value, index) => (
            <div
              key={index}
              className={`mb-4 p-3 border rounded ${selectedValues.has(index) ? 'border-primary bg-light' : 'opacity-50'}`}
              style={{ cursor: 'pointer' }}
              onClick={() => toggleValueSelection(index)}
            >
              <div className="d-flex align-items-start">
                <Form.Check
                  type="checkbox"
                  checked={selectedValues.has(index)}
                  onChange={() => toggleValueSelection(index)}
                  onClick={(e) => e.stopPropagation()}
                  className="me-3 mt-1"
                  style={{ transform: 'scale(1.3)' }}
                />
                <div className="flex-grow-1">
                  <h4 className="mb-2">{value.value_name}</h4>

                  <div className="mb-2">
                    <strong>What It Means:</strong>
                    <p className="mb-1">{value.definition}</p>
                  </div>

                  <div className="mb-2">
                    <strong>In Practice:</strong>
                    <ul className="mb-1">
                      {Array.isArray(value.in_practice) ? value.in_practice.map((practice, i) => (
                        <li key={i}>{practice}</li>
                      )) : (
                        <li>{value.in_practice}</li>
                      )}
                    </ul>
                  </div>

                  {value.conversation_evidence && (
                    <div>
                      <strong>You Said:</strong>
                      <p className="fst-italic mb-0">"{value.conversation_evidence}"</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          <Alert variant="info" className="mt-3">
            <strong>{selectedValues.size}</strong> of {extractedValues.length} values selected
          </Alert>

          <div className="d-flex justify-content-between mt-4">
            <Button variant="outline-secondary" onClick={refineValues}>
              Let Me Refine Them
            </Button>
            <Button
              variant="primary"
              onClick={acceptSelectedValues}
              disabled={selectedValues.size === 0}
            >
              Save {selectedValues.size} Selected Value{selectedValues.size !== 1 ? 's' : ''}
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  };

  const renderComplete = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">CORE VALUES DEFINED</h2>

        <Alert variant="success">
          Your core values have been saved!
        </Alert>

        <div className="mb-4">
          {extractedValues.map((value, index) => (
            <div key={index} className="mb-3">
              <h5>{index + 1}. {value.value_name}</h5>
              <p className="text-muted">{value.definition}</p>
            </div>
          ))}
        </div>

        <div className="p-3 bg-light rounded mb-4">
          <p className="mb-2"><strong>These values will guide:</strong></p>
          <ul className="mb-0">
            <li>Your mission and vision statements</li>
            <li>Hiring decisions (do they share these beliefs?)</li>
            <li>Product development (which features align with values?)</li>
            <li>Marketing messaging (authentic, not generic)</li>
            <li>Difficult decisions (when in doubt, which value wins?)</li>
          </ul>
        </div>

        {/* Auto-extracted Mission & Vision */}
        {(missionVisionLoading || extractedMission || extractedVision) && !missionVisionSaved && (
          <Card className="mb-4 border-warning">
            <Card.Header className="bg-warning">
              <strong>✨ Bonus: Mission & Vision Extracted!</strong>
            </Card.Header>
            <Card.Body>
              {missionVisionLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="warning" className="me-2" />
                  <span>Extracting mission & vision from your conversation...</span>
                </div>
              ) : (
                <>
                  <p className="text-muted mb-3">
                    Based on what you shared, we drafted a Mission and Vision statement. Edit if needed, then approve to save - or discard to write your own later.
                  </p>

                  <Form.Group className="mb-4">
                    <Form.Label><strong>Mission Statement</strong> (Your purpose - what you do and for whom)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={editedMission}
                      onChange={(e) => setEditedMission(e.target.value)}
                      placeholder="Your mission statement..."
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label><strong>Vision Statement</strong> (The future you're working to create)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={editedVision}
                      onChange={(e) => setEditedVision(e.target.value)}
                      placeholder="Your vision statement..."
                    />
                  </Form.Group>

                  <div className="d-flex gap-2">
                    <Button
                      variant="success"
                      onClick={saveMissionVision}
                      disabled={!editedMission && !editedVision}
                    >
                      ✓ Approve & Save
                    </Button>
                    <Button variant="outline-secondary" onClick={discardMissionVision}>
                      Discard - I'll Write My Own
                    </Button>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        )}

        {missionVisionSaved && (
          <Alert variant="success" className="mb-4">
            <strong>✓ Mission & Vision saved!</strong> Two more items checked off your Foundation list.
          </Alert>
        )}

        {/* View Conversation Transcript */}
        <Card className="mb-4 border-info">
          <Card.Header
            className="bg-info text-white d-flex justify-content-between align-items-center"
            style={{ cursor: 'pointer' }}
            onClick={() => showTranscript ? setShowTranscript(false) : loadSavedConversation()}
          >
            <strong>💬 Full Conversation Transcript</strong>
            <small>{showTranscript ? '(click to hide)' : '(click to view)'}</small>
          </Card.Header>
          {showTranscript && (
            <Card.Body style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <p className="text-muted small mb-3">
                <em>This is your complete discovery conversation - great for pulling authentic stories,
                quotes, and AI reframes into your marketing content!</em>
              </p>
              {savedConversation.length > 0 ? (
                savedConversation.map((msg, index) => (
                  <div
                    key={index}
                    className={`mb-3 p-3 rounded ${msg.role === 'assistant' ? 'bg-light' : 'bg-white border'}`}
                  >
                    <strong className={msg.role === 'assistant' ? 'text-primary' : 'text-success'}>
                      {msg.role === 'assistant' ? '🤖 AI Coach:' : '👤 You:'}
                    </strong>
                    <div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                  </div>
                ))
              ) : (
                <p className="text-muted">Loading conversation...</p>
              )}
            </Card.Body>
          )}
        </Card>

        <div className="d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={() => navigate('/dashboard/foundation')}>
            Back to Dashboard
          </Button>
          <Button variant="primary" onClick={() => navigate('/target-market-discovery')}>
            Continue to Target Market →
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  return (
    <Container className="py-5">
      <BackToDashboard module="foundation" />
      <Row className="justify-content-center">
        <Col md={10} lg={8}>
          {stage === 'intro' && renderIntro()}
          {stage === 'conversation' && renderConversation()}
          {stage === 'extracted' && renderExtractedValues()}
          {stage === 'complete' && renderComplete()}
        </Col>
      </Row>
    </Container>
  );
}

export default CoreValues;
